import {
  WIZARD_STEPS,
  type FlowStep,
  type SessionState,
  type WizardStep,
} from '../types/session.types'

/**
 * Guard decisions as pure functions of (params, state).
 *
 * Keeping the logic out of the components means the interesting cases — a stale
 * bookmark, a refresh after the TTL, a back-button escape from the dead-end — are
 * table-testable without rendering anything.
 */

export type GuardDecision =
  | { allow: true }
  | { allow: false; redirectTo: 'start'; reason: 'no_session' | 'ref_mismatch' }
  | { allow: false; redirectTo: 'verify' }
  | { allow: false; redirectTo: 'blocked' }
  | { allow: false; redirectTo: 'step'; step: WizardStep }

export interface GuardInput {
  /** appRefNo from the URL. */
  paramRef: string | undefined
  state: Pick<SessionState, 'appRefNo'>
}

/**
 * Is there a live application, and does the URL refer to it?
 *
 * Deliberately says NOTHING about the inactivity TTL, though it used to. Expiry is
 * applied by `expireIfStale()` in the store, which empties the session outright — so
 * by the time this runs, an expired session is indistinguishable from no session and
 * falls out of the first branch below.
 *
 * Splitting it that way is what fixes the redirect loop. When this function reported
 * expiry itself, it bounced the citizen to `/apply`, where the resume selector — which
 * has no notion of the TTL — read the still-present appRefNo and sent them right back
 * to the step they came from. Neither side could break the cycle. Now there is one
 * authority for expiry and it produces one terminal destination.
 *
 * It is also no longer a function of the clock, which kept `Date.now()` in a
 * component's render path: two renders straddling the TTL boundary disagreed, and
 * under StrictMode or a discarded concurrent render only the last was committed.
 */
export function checkSession({ paramRef, state }: GuardInput): GuardDecision {
  if (!state.appRefNo) {
    return { allow: false, redirectTo: 'start', reason: 'no_session' }
  }

  // A bookmarked or shared link pointing at someone else's application.
  if (paramRef !== undefined && paramRef !== state.appRefNo) {
    return { allow: false, redirectTo: 'start', reason: 'ref_mismatch' }
  }

  return { allow: true }
}

/** Face verification gates the whole form, and its dead-end must be inescapable. */
export function checkFaceVerified(
  state: Pick<SessionState, 'faceBlocked' | 'faceVerified'>
): GuardDecision {
  if (state.faceBlocked) return { allow: false, redirectTo: 'blocked' }
  if (!state.faceVerified) return { allow: false, redirectTo: 'verify' }
  return { allow: true }
}

/**
 * Has the applicant completed everything before this step?
 *
 * On failure we send them to the earliest step they have *not* finished, rather
 * than back to the start. A mistimed refresh should cost a click, not the whole
 * application.
 */
export function checkStepOrder(target: WizardStep, completedSteps: WizardStep[]): GuardDecision {
  const targetIndex = WIZARD_STEPS.indexOf(target)

  for (let index = 0; index < targetIndex; index += 1) {
    const prerequisite = WIZARD_STEPS[index]!
    if (!completedSteps.includes(prerequisite)) {
      return { allow: false, redirectTo: 'step', step: prerequisite }
    }
  }

  return { allow: true }
}

/**
 * May this application reach the receipt?
 *
 * `checkStepOrder` cannot express this. It only checks the steps *before* its
 * target, and `declaration` is the last entry in WIZARD_STEPS, so
 * `RequireStep step="declaration"` would wave through an application whose
 * declaration was never signed.
 *
 * That matters more here than on any other route: the receipt fires §4.10
 * finalise on mount, and §4.10 is not idempotent (docs/api-gaps.md #8). A deep
 * link to it is therefore a submission, not a page view.
 *
 * The redirect target matches checkStepOrder's — the earliest incomplete step —
 * so it is terminal and cannot bounce back to here.
 */
export function checkSubmittedAccess(completedSteps: WizardStep[]): GuardDecision {
  if (completedSteps.includes('declaration')) return { allow: true }

  return { allow: false, redirectTo: 'step', step: firstIncompleteStep(completedSteps) }
}

/** The step a resumed application should land on. */
export function firstIncompleteStep(completedSteps: WizardStep[]): WizardStep {
  return WIZARD_STEPS.find((step) => !completedSteps.includes(step)) ?? 'declaration'
}

// ---------------------------------------------------------------------------
// Whole-flow resume
// ---------------------------------------------------------------------------

export type FlowResumeState = Pick<
  SessionState,
  'appRefNo' | 'faceVerified' | 'faceBlocked' | 'completedSteps' | 'submittedAt'
>

/**
 * Where a resumed journey should land, across the whole ten-step flow. The
 * wizard-only firstIncompleteStep still exists because the DHA-backed subset is
 * what checkStepOrder enforces; this one fronts it with the entry screen and
 * face verification, and closes it with the receipt.
 */
export function firstIncompleteFlowStep(state: FlowResumeState): FlowStep {
  // No application yet: the ID-number screen is where eligibility is confirmed
  // and the reference is minted. A blocked applicant still resolves to 'face' —
  // the route guard on the camera page forwards them to the dead-end.
  if (!state.appRefNo) return 'idNumber'
  if (!state.faceVerified) return 'face'
  if (state.submittedAt) return 'submitted'
  const wizardStep = firstIncompleteStep(state.completedSteps)
  if (wizardStep === 'declaration' && state.completedSteps.includes('declaration')) {
    return 'submitted'
  }
  return wizardStep
}
